/**
 * VoiceBar — persistent voice/call indicator at the bottom of the sidebar.
 *
 * Shows either:
 *  - Voice channel connection (existing)
 *  - DM call (dialing / connected)
 *
 * Manages <audio> elements for all remote streams so they survive navigation.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, PhoneOff, Phone, Volume2, ChevronUp, Clock, Monitor, Video, VideoOff } from 'lucide-react';
import { useVoiceStore } from '../../store/voiceStore';
import { useServerStore } from '../../store/serverStore';
import { useMessageStore } from '../../store/messageStore';
import { playMute, playUnmute } from '../../lib/sounds';
import CallExpandedPanel from './CallExpandedPanel';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const VoiceBar: React.FC = () => {
  const {
    // Channel voice
    isConnected,
    currentChannelId,
    currentChannelName,
    connectedUsers,
    remoteStreams,
    leaveVoiceChannel,
    // DM call
    dmCallStatus,
    dmCallUsers,
    dmCallRemoteStreams,
    callDuration,
    leaveDmCall,
    // Shared
    isMuted,
    isCameraOn,
    isScreenSharing,
    localStream,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    // Expanded panel
    isCallPanelExpanded,
    setCallPanelExpanded,
  } = useVoiceStore();

  const { setCurrentChannel, channels } = useServerStore();
  const { setCurrentDmUser } = useMessageStore();

  // merge remote streams from both channel and DM
  const allRemoteStreams = isConnected ? remoteStreams : dmCallRemoteStreams;

  // Remote audio element refs
  const remoteAudioRefs = useRef<Record<string, HTMLAudioElement>>({});

  // Local speaking level
  const [audioLevel, setAudioLevel] = useState(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);

  const isActive = isConnected || dmCallStatus !== 'idle';

  // ── Remote audio management ──────────────────────────────────────────────
  useEffect(() => {
    for (const [userId, stream] of Object.entries(allRemoteStreams)) {
      if (!remoteAudioRefs.current[userId]) {
        const audio = new Audio();
        audio.autoplay = true;
        audio.srcObject = stream;
        remoteAudioRefs.current[userId] = audio;
        audio.play().catch(() => {});
      } else if (remoteAudioRefs.current[userId].srcObject !== stream) {
        remoteAudioRefs.current[userId].srcObject = stream;
      }
    }
    for (const userId of Object.keys(remoteAudioRefs.current)) {
      if (!allRemoteStreams[userId]) {
        remoteAudioRefs.current[userId].srcObject = null;
        delete remoteAudioRefs.current[userId];
      }
    }
  }, [allRemoteStreams]);

  // ── Local speaking level analyser ─────────────────────────────────────────
  useEffect(() => {
    if (!localStream || !isActive) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      analyserRef.current = null;
      setAudioLevel(0);
      return;
    }

    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(localStream).connect(analyser);
      analyserRef.current = analyser;

      const tick = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setAudioLevel(Math.min(avg / 128, 1));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);

      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        ctx.close();
      };
    } catch {
      // ignore
    }
  }, [localStream, isActive]);

  // Cleanup all audio on disconnect
  useEffect(() => {
    if (!isActive) {
      for (const audio of Object.values(remoteAudioRefs.current)) {
        audio.srcObject = null;
      }
      remoteAudioRefs.current = {};
    }
  }, [isActive]);

  if (!isActive) return null;

  const handleToggleMute = () => {
    if (isMuted) playUnmute(); else playMute();
    toggleMute();
  };

  const handleDisconnect = () => {
    if (isConnected) leaveVoiceChannel();
    else leaveDmCall();
  };

  // ── Channel voice mode ──────────────────────────────────────────────────
  if (isConnected) {
    const handleGoToChannel = () => {
      if (!currentChannelId) return;
      const channel = channels.find((c) => c.id === currentChannelId);
      if (channel) {
        setCurrentDmUser(null);
        setCurrentChannel(channel);
      }
    };

    return (
      <div className="flex-shrink-0 border-t border-border-color bg-bg-tertiary px-3 py-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-2 h-2 rounded-full bg-success flex-shrink-0 animate-pulse" />
            <span className="text-xs text-success font-semibold">Voix connecté</span>
          </div>
          {!isMuted && (
            <div className="w-16 h-1.5 bg-bg-primary rounded-full overflow-hidden flex-shrink-0">
              <div
                className="h-full bg-success transition-all duration-75"
                style={{ width: `${audioLevel * 100}%` }}
              />
            </div>
          )}
        </div>

        <button
          className="flex items-center gap-1.5 w-full text-left mb-2 group"
          onClick={handleGoToChannel}
          title="Aller au canal vocal"
        >
          <Volume2 className="h-3.5 w-3.5 text-text-secondary flex-shrink-0 group-hover:text-accent-color transition-colors" />
          <span className="text-sm font-medium text-text-primary truncate group-hover:text-accent-color transition-colors">
            {currentChannelName ?? 'Voice Channel'}
          </span>
          <span className="text-xs text-text-muted flex-shrink-0">
            {connectedUsers.length + 1}
          </span>
        </button>

        <div className="flex items-center gap-1">
          <button
            onClick={handleToggleMute}
            title={isMuted ? 'Désactiver le muet' : 'Muet'}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              isMuted
                ? 'bg-error/20 text-error hover:bg-error/30'
                : 'bg-highlight hover:bg-highlight-hover text-text-primary'
            }`}
          >
            {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            {isMuted ? 'Muet' : 'Micro'}
          </button>
          <button
            onClick={() => void toggleScreenShare()}
            title={isScreenSharing ? "Arrêter le partage d'écran" : "Partager l'écran"}
            className={`flex items-center justify-center p-1.5 rounded-md transition-colors ${
              isScreenSharing
                ? 'bg-success/20 text-success hover:bg-success/30'
                : 'bg-bg-tertiary/60 hover:bg-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            <Monitor className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={handleDisconnect}
            title="Quitter le canal vocal"
            className="flex items-center justify-center p-1.5 rounded-md bg-error/10 hover:bg-error/20 text-error transition-colors"
          >
            <PhoneOff className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // ── DM call mode ──────────────────────────────────────────────────────────
  const isDialing = dmCallStatus === 'dialing';
  const firstUser = dmCallUsers[0];
  const displayName = firstUser?.username ?? 'Appel DM';

  const handleGoToDm = () => {
    if (firstUser) {
      setCurrentDmUser(firstUser.id);
    }
  };

  return (
    <div className="flex-shrink-0 border-t border-border-color bg-bg-tertiary px-3 py-2 relative">
      {/* Expanded panel (popup above) */}
      <CallExpandedPanel />

      {/* Call status row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              isDialing ? 'bg-warning animate-pulse' : 'bg-success animate-pulse'
            }`}
          />
          <span
            className={`text-xs font-semibold ${
              isDialing ? 'text-warning' : 'text-success'
            }`}
          >
            {isDialing ? 'Appel en cours...' : 'En appel'}
          </span>
          {dmCallStatus === 'connected' && (
            <span className="flex items-center gap-0.5 text-xs text-text-secondary">
              <Clock className="w-3 h-3" />
              {formatDuration(callDuration)}
            </span>
          )}
        </div>
        {!isMuted && dmCallStatus === 'connected' && (
          <div className="w-16 h-1.5 bg-bg-primary rounded-full overflow-hidden flex-shrink-0">
            <div
              className="h-full bg-success transition-all duration-75"
              style={{ width: `${audioLevel * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* User name + expand button */}
      <div className="flex items-center gap-1.5 mb-2">
        <button
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left group"
          onClick={handleGoToDm}
          title="Aller à la conversation"
        >
          <Phone className="h-3.5 w-3.5 text-text-secondary flex-shrink-0 group-hover:text-accent-color transition-colors" />
          <span className="text-sm font-medium text-text-primary truncate group-hover:text-accent-color transition-colors">
            {displayName}
          </span>
          {dmCallUsers.length > 1 && (
            <span className="text-xs text-text-muted flex-shrink-0">
              +{dmCallUsers.length - 1}
            </span>
          )}
        </button>
        <button
          onClick={() => setCallPanelExpanded(!isCallPanelExpanded)}
          className="p-1 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary transition-colors"
          title="Détails de l'appel"
        >
          <ChevronUp
            className={`w-3.5 h-3.5 transition-transform ${
              isCallPanelExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1">
        <button
          onClick={handleToggleMute}
          title={isMuted ? 'Désactiver le muet' : 'Muet'}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
            isMuted
              ? 'bg-error/20 text-error hover:bg-error/30'
              : 'bg-highlight hover:bg-highlight-hover text-text-primary'
          }`}
        >
          {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
          {isMuted ? 'Muet' : 'Micro'}
        </button>
        {/* Camera toggle — DM call only */}
        <button
          onClick={() => void toggleCamera()}
          title={isCameraOn ? 'Couper la caméra' : 'Activer la caméra'}
          className={`flex items-center justify-center p-1.5 rounded-md transition-colors ${
            isCameraOn
              ? 'bg-accent-color/20 text-accent-color hover:bg-accent-color/30'
              : 'bg-bg-tertiary/60 hover:bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {isCameraOn ? <Video className="h-3.5 w-3.5" /> : <VideoOff className="h-3.5 w-3.5" />}
        </button>
        {/* Screen share */}
        <button
          onClick={() => void toggleScreenShare()}
          title={isScreenSharing ? "Arrêter le partage" : "Partager l'écran"}
          className={`flex items-center justify-center p-1.5 rounded-md transition-colors ${
            isScreenSharing
              ? 'bg-success/20 text-success hover:bg-success/30'
              : 'bg-bg-tertiary/60 hover:bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          <Monitor className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={handleDisconnect}
          title="Raccrocher"
          className="flex items-center justify-center p-1.5 rounded-md bg-error/10 hover:bg-error/20 text-error transition-colors"
        >
          <PhoneOff className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default VoiceBar;
